import {
    useEffect,
    useRef,
    useState
} from "react";
import {EmailAddressForm} from "../forms/EmailAddressForm.tsx";
import {useWindowScroll} from "@mantine/hooks";
import {LinkToSignUpPage} from "../../sign-up/components/LinkToSignUpPage.tsx";
import {LinkToResetPasswordPage} from "../../reset-password/components/LinkToResetPasswordPage.tsx";
import {SignInPageGrid} from "../components/SignInPageGrid.tsx";
import {useAuth} from "../../../../hooks/useAuth.ts";
import {Navigate} from "react-router-dom";
import {Cache} from "aws-amplify/utils";
import {PlatformLoader} from "@flexinet/ui-components";
import {useUserAccount} from "../../../../hooks/useUserAccount.ts";
import {isAllowedRedirectUrl} from "../../../../utils/isAllowedRedirectUrl.ts";
import {
    MintAlreadyInFlightError,
    mintAuthorizationCode
} from "../../../../utils/mintAuthorizationCode.ts";
import {isAuthBrokerAvailable} from "../../../../config/authBroker.ts";
import {loadFreshSession} from "../../../../utils/loadFreshSession.ts";

export const IndexPage = () => {

    const [destination, setDestination] = useState('')

    // Serialises redirectAuthenticated, which consumes a single-use parked
    // request on the code path. Claimed in the effect below — see the note there
    // for why it cannot be claimed inside the async function.
    const runStartedRef = useRef(false)

    const [, scrollTo] = useWindowScroll();

    const {
        user,
        idToken,
        accessToken,
    } = useAuth()

    const {userAccount} = useUserAccount()

    const redirectAuthenticated = async () => {

        const authorization = await Cache.getItem('authorization')

        if (typeof user !== 'undefined' && typeof idToken !== 'undefined' && typeof accessToken !== 'undefined') {

            // FNP-864: the cache is UNTRUSTED input here. It is written before
            // the user authenticates, so a signed-out visitor following a
            // crafted link can seed it with an arbitrary origin. Re-validate
            // against the allowlist immediately before emitting tokens.
            if (authorization && isAllowedRedirectUrl(authorization.redirectUrl, authorization.responseType)) {

                // ── FNP-871/872: THE SAME FORK AS AuthorizePage ──────────────
                //
                // This path runs when the user arrived SIGNED OUT: AuthorizePage
                // cached the request and handed off to this sign-in page, which
                // completes the redirect itself and never returns there. So the
                // code-flow fork has to exist here too — without it a migrated
                // app silently gets the fragment relay it asked not to receive,
                // and lands on the broker's callback with raw tokens it cannot
                // use.
                if (authorization.responseType === 'code') {

                    if (!isAuthBrokerAvailable() || !authorization.state) {
                        await Cache.removeItem('authorization')
                        setDestination('dashboard')
                        return
                    }

                    try {
                        // `state` here is the broker's REQUEST ID; the app's own
                        // state is parked broker-side and restored by the broker.
                        // Read the session directly, for the same reason as
                        // AuthorizePage: useAuth() hydrates across separate
                        // effects and this component remounts while it settles.
                        // FNP-1358: refreshed against Cognito first, and
                        // carrying the refresh token.
                        const session = await loadFreshSession()
                        if (session.status === 'unavailable') {
                            // Transient, session intact: same as any other
                            // failed hand-off. Signing in again would not help.
                            await Cache.removeItem('authorization')
                            setDestination('dashboard')
                            return
                        }
                        if (session.status !== 'fresh') {
                            // The context says signed in, the store disagrees.
                            // Reload the sign-in page on the cleared store so the
                            // user signs in again. The cached request is kept, so
                            // the mint happens after they do.
                            window.location.assign('/sign-in')
                            return
                        }

                        const redirectTo = await mintAuthorizationCode({
                            caller: 'IndexPage',
                            requestId: authorization.state,
                            idToken: session.idToken,
                            accessToken: session.accessToken,
                            refreshToken: session.refreshToken,
                        })
                        await Cache.removeItem('authorization')

                        // Navigate directly rather than via setDestination:
                        // a state update needs this mount to still be alive,
                        // and it frequently is not.
                        window.location.assign(redirectTo)
                    } catch (error) {
                        // Already in flight elsewhere — leave the loader up and
                        // let the winning caller navigate. Sending this mount to
                        // the dashboard would abandon a sign-in in progress.
                        if (error instanceof MintAlreadyInFlightError) {
                            return
                        }
                        // Never log the error — the request carried tokens.
                        await Cache.removeItem('authorization')
                        setDestination('dashboard')
                    }
                    return
                }

                // Never log this URL — it carries both raw tokens.
                const returnUrl = `${authorization.redirectUrl}#id_token=${idToken}&access_token=${accessToken}&token_type=Bearer&expires_in=3600&state=${authorization.state}`
                setDestination(returnUrl)
                await Cache.removeItem('authorization')

            } else {

                // Fail closed: drop anything unrecognised and go to the dashboard.
                await Cache.removeItem('authorization')
                setDestination('dashboard')

            }
        }
    }


    useEffect(() => {
        // Serialise at the EFFECT, synchronously, before anything async runs.
        //
        // This effect is keyed on three values that settle at different times,
        // so it fires several times per sign-in, and StrictMode doubles that in
        // dev. `redirectAuthenticated` awaits `Cache.getItem` on its first line,
        // so a claim taken inside it would come too late — both invocations
        // would already be past the read. Claiming here is the only point that
        // is genuinely synchronous with respect to the effect.
        //
        // The symptom this fixes, straight from the broker log: `issued`
        // followed 600ms later by `unknown_request` — the first call minted a
        // code and consumed the single-use parked request, the second found it
        // gone and failed a sign-in that had already succeeded.
        // NOTE: this only avoids redundant work. The real single-mint
        // guarantee lives in mintAuthorizationCode's sessionStorage claim,
        // which survives the remounts a ref cannot.
        if (runStartedRef.current) {
            return
        }
        if (typeof user === 'undefined'
            || typeof idToken === 'undefined'
            || typeof accessToken === 'undefined') {
            // Nothing to do yet — do NOT claim, or the real run is blocked.
            return
        }
        runStartedRef.current = true

        redirectAuthenticated()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, idToken, accessToken])


    useEffect(() => {
        scrollTo({y: 0})
    }, [scrollTo]);

    // if (typeof user !== 'undefined' && typeof idToken !== 'undefined' && typeof accessToken !== 'undefined') {
    if (userAccount) {
        // navigate('/dashboard')

        if (destination === '') {
            return <PlatformLoader/>
        } else {
            if (destination === 'dashboard') {
                return <Navigate to={'/dashboard'}/>
            } else {
                window.location.assign(destination)
            }
        }

    } else {

        return (
            <SignInPageGrid>
                <EmailAddressForm/>
                <LinkToSignUpPage/>
                <LinkToResetPasswordPage/>
            </SignInPageGrid>
        );

    }
}
